import {
  FiHome, FiTarget, FiLayers, FiEdit3, FiAward, FiUsers, FiCheckSquare, FiClipboard,
  FiUserCheck, FiCalendar, FiBarChart2, FiSliders, FiShield, FiLink,
  FiStar, FiFlag, FiMessageSquare, FiBell, FiSquare, FiBriefcase,
  FiPaperclip, FiTrendingUp, FiInbox, FiClock, FiActivity,FiThumbsUp,
  
} from "react-icons/fi";

export const APP_NAME = "Teliolabs PMS";

export const STORAGE_KEYS = {
  accessToken: "access_token",
  user: "user",
  role: "pms_role",
  username: "pms_username",
  activeView: "pms_active_view",
  selectedCycle: "pms_selected_cycle",
  permissions: "permissions",
  roles: "roles",
  groupId: "group_id",
  lastRoute: "lastRoute",
  sessionExpiresAt: "pms_session_expires_at",
};

export const ROLES = {
  EMPLOYEE: "employee",
  MANAGER: "manager",
  HR: "hr",
  MANAGEMENT: "management",
  ADMIN: "admin",
  IT_ADMIN: "it_admin", // [NEW] PM-5
};

export const ROLE_LABELS = {
  employee: "Employee",
  manager: "Manager",
  hr: "HR",
  management: "Management",
  admin: "Admin",
  it_admin: "IT Admin", // [NEW] PM-5: Admin view limited to users and audit trail
};

export const VIEWS = {
  EMPLOYEE: "employee",
  MANAGER: "manager",
  HR: "hr",
  MANAGEMENT: "management",
  ADMIN: "admin",
};

export const VIEW_LABELS = {
  employee: "Employee",
  manager: "Manager",
  hr: "HR",
  management: "Management",
  admin: "Admin",
};

export function getAvailableViews(roleKeys) {
  const keys = Array.isArray(roleKeys) ? roleKeys : [roleKeys];
  const views = [];
  if (keys.includes(ROLES.ADMIN)) views.push(VIEWS.ADMIN, VIEWS.HR);
  if (keys.includes(ROLES.HR) && !views.includes(VIEWS.HR)) views.push(VIEWS.HR);
  if (keys.includes(ROLES.MANAGEMENT)) views.push(VIEWS.MANAGEMENT);
  if (keys.includes(ROLES.MANAGER)) views.push(VIEWS.MANAGER);
  views.push(VIEWS.EMPLOYEE);
  // [NEW] PM-5: IT Admin gets the Admin view (only Users & roles and Audit trail). Added last, so the person still
  // lands on their own dashboard after signing in.
  if (keys.includes(ROLES.IT_ADMIN) && !views.includes(VIEWS.ADMIN)) views.push(VIEWS.ADMIN);
  return views;
}

const flag = (name) => String(process.env[name] || "").toLowerCase() === "true";
export const FEATURES = {
  competency: flag("REACT_APP_FEATURE_COMPETENCY"),
  calibration: flag("REACT_APP_FEATURE_CALIBRATION"),
  feedback: flag("REACT_APP_FEATURE_FEEDBACK"),
};

export const getDefaultRoute = (permissions = [], roles = []) => "/dashboard";

export const saveLastRoute = (path) => {
  localStorage.setItem(STORAGE_KEYS.lastRoute, path);
};

export const getLastRoute = () => localStorage.getItem(STORAGE_KEYS.lastRoute);

// [UPDATED] PM-19: one sidebar for every view. The sidebar shows one block per view the user has
// (Admin, HR, Management, My team, My work); clicking an item switches the active view itself,
// so there is no "View as" switch any more. PM-6: menu names follow the approved naming list.
export const VIEW_SECTION_LABELS = {
  admin: "Admin",
  hr: "HR",
  management: "Management",
  manager: "My team",
  employee: "My work",
};

export const NAVIGATION = {
  [VIEWS.ADMIN]: [
    {
      section: "Admin",
      items: [
        { label: "Admin dashboard", path: "/dashboard", icon: FiHome },
        { label: "Users & roles", path: "/admin/users", icon: FiShield, itAdmin: true },
        // [NEW] Audit trail is shown to IT Admin only (not to Admin).
        //{ label: "Audit trail", path: "/audit-trail", icon: FiClipboard, itAdmin: true, itAdminOnly: true },
        { label: "Scoring & ratings", path: "/hr/rating-settings", icon: FiSliders },
        { label: "Organisation setup", path: "/hr/organisation", icon: FiBriefcase },
        { label: "Audit trail", path: "/audit-trail", icon: FiClock, itAdmin: true, itAdminOnly: true },
        // { label: "Monitoring", path: "/monitoring", icon: FiActivity, itAdmin: true },
      ],
    },
  ],
  [VIEWS.HR]: [
    {
      section: "HR",
      items: [
        { label: "HR dashboard", path: "/dashboard", icon: FiHome },
        { label: "Employees", path: "/hr/employees", icon: FiUsers },
        { label: "Performance cycles", path: "/hr/cycles", icon: FiCalendar },
        // [NEW] HR can open Organisation setup from their own menu, same page as the Admin item.
        { label: "Organisation setup", path: "/hr/organisation", icon: FiBriefcase, dupOfAdmin: true },
        // [NEW] HR can open Scoring & ratings (weights and rating bands) from their own menu, same page as the Admin item.
        { label: "Scoring & ratings", path: "/hr/rating-settings", icon: FiSliders, dupOfAdmin: true },
        // [UPDATED] PM-15/PM-16: HR reviews and edits KRAs, KPIs and goals; HR no longer assigns them.
        { label: "KRA, KPI & goal review", path: "/hr/kra-kpi", icon: FiLink },
        // [UPDATED] 21 Oct 2026: KRA library, KPI library and Role templates removed from the HR menu (on request).
        { label: "Review & finalise", path: "/hr/review", icon: FiUserCheck },
        // [NEW] Requirement 7: employees not moved to a PIP after the final rating are applicable for the appraisal.
        { label: "Appraisal", path: "/appraisal", icon: FiThumbsUp },
        { label: "Calibration", path: "/calibration", icon: FiSquare, feature: "calibration" },
        { label: "Competencies", path: "/competencies", icon: FiStar, feature: "competency" },
        { label: "Feedback", path: "/feedback", icon: FiMessageSquare, feature: "feedback" },
        { label: "Improvement plans (PIP)", path: "/pip", icon: FiFlag },
        { label: "Reminders", path: "/hr/reminders", icon: FiBell },
        { label: "Reports", path: "/reports", icon: FiBarChart2 },
        // [REMOVED] Requirement 4: "Evidence" is not in the HR menu; only employees (My evidence) and managers (Team evidence) see it.
        // [UPDATED] PM-8: HR sees only their own audit trail (My activity in My work); the full audit trail is Admin / IT Admin only.
      ],
    },
  ],
  [VIEWS.MANAGEMENT]: [
    {
      section: "Management",
      items: [
        { label: "Organisation dashboard", path: "/dashboard", icon: FiHome },
        { label: "Calibration", path: "/calibration", icon: FiSquare, feature: "calibration" },
        { label: "Reports", path: "/reports", icon: FiBarChart2 },
      ],
    },
  ],
  [VIEWS.MANAGER]: [
    {
      section: "My team",
      items: [
        { label: "Team dashboard", path: "/dashboard", icon: FiHome },
        { label: "Team members", path: "/team", icon: FiUsers },
        { label: "Goal approvals", path: "/team/goal-approvals", icon: FiCheckSquare, badgeKey: "pendingApprovals" },
        // [UPDATED] PM-17: managers approve, review and edit what employees submit. The manager KRA/KPI
        // libraries, team templates and "Assign KRA & KPI" are removed from the menu.
        { label: "KRA & KPI approvals", path: "/team/kra-kpi-proposals", icon: FiInbox, permission: "kra_kpi.team.review" },
        { label: "Progress approvals", path: "/team/progress-approvals", icon: FiTrendingUp, permission: "progress.team.approve", badgeKey: "pendingProgress" },
        { label: "Team assessments", path: "/team/assessments", icon: FiClipboard, badgeKey: "pendingAssessments" },
        { label: "Competencies", path: "/competencies", icon: FiStar, feature: "competency" },
        { label: "Feedback", path: "/feedback", icon: FiMessageSquare, feature: "feedback" },
        { label: "Improvement plans (PIP)", path: "/pip", icon: FiFlag },
        { label: "Team appraisal", path: "/appraisal", icon: FiThumbsUp }, // [NEW] requirement 7
        { label: "Reminders", path: "/team/reminders", icon: FiBell },
        { label: "Team reports", path: "/reports", icon: FiBarChart2, permission: "team.report.read" },
        { label: "Team evidence", path: "/evidence", icon: FiPaperclip },
      ],
    },
  ],
  [VIEWS.EMPLOYEE]: [
    {
      section: "My work",
      items: [
        { label: "My dashboard", path: "/dashboard", icon: FiHome },
        { label: "My goals", path: "/my-goals", icon: FiTarget, badgeKey: "myGoalsUpdate" }, // [NEW] badge when the manager approves / sends back the goals
        { label: "My KRA & KPI", path: "/my-kra-kpi", icon: FiLayers },
        { label: "My competencies", path: "/competencies", icon: FiStar, feature: "competency" },
        { label: "Self assessment", path: "/self-assessment", icon: FiEdit3 },
        { label: "My results", path: "/my-results", icon: FiAward },
        { label: "Feedback", path: "/feedback", icon: FiMessageSquare, feature: "feedback" },
        { label: "Improvement plan", path: "/pip", icon: FiFlag },
        { label: "My appraisal", path: "/appraisal", icon: FiThumbsUp }, // [NEW] requirement 7
        { label: "My evidence", path: "/evidence", icon: FiPaperclip },
        // [NEW] PM-8: employees see only their own audit trail
        { label: "My activity", path: "/my-activity", icon: FiClock },
      ],
    },
  ],
};

// [NEW] PM-19: every view the user has, in sidebar order, each with its menu items.
export function sidebarFor(availableViews, grants = null, roleKeys = []) {
  // PM-5: someone who is IT Admin but not Admin sees only the Admin items marked itAdmin.
  const itOnly = roleKeys.includes(ROLES.IT_ADMIN) && !roleKeys.includes(ROLES.ADMIN);
  return SIDEBAR_ORDER.filter((view) => availableViews.includes(view))
    .map((view) => ({
      view,
      label: view === VIEWS.ADMIN && itOnly ? "IT Admin" : VIEW_SECTION_LABELS[view],
      items: navigationFor(view, grants)
        .flatMap((s) => s.items)
        .filter((item) => {
          if (view !== VIEWS.ADMIN) return true;
          if (itOnly) return item.itAdmin;
          return !item.itAdminOnly;
        })
        // Admins already have these items in the Admin block, so do not repeat them under HR.
        .filter(
          (item) =>
            !(view === VIEWS.HR && item.dupOfAdmin && roleKeys.includes(ROLES.ADMIN))
        ),
    }))
    .filter((block) => block.items.length);
}

export const SIDEBAR_ORDER = [VIEWS.ADMIN, VIEWS.HR, VIEWS.MANAGEMENT, VIEWS.MANAGER, VIEWS.EMPLOYEE];

export function navigationFor(view, grants = null) {
  const allowed = (item) => !item.permission || !grants || grants.some((g) => g.permission_code === item.permission);
  return (NAVIGATION[view] || [])
    .map((section) => ({ ...section, items: section.items.filter((item) => (!item.feature || FEATURES[item.feature]) && allowed(item)) }))
    .filter((section) => section.items.length);
}
